import { skipToken, useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';

import type * as api from '@gitbook/api-client';
import { getIntegrationInstallationKey } from '@gitbook/app-database';
import { assertIsDefined } from '@gitbook/util-ts';

import {
    assertIsExtendedSiteInstallation,
    assertIsExtendedSpaceInstallation,
} from '../../../Components';
import { useNavigation } from '../../../navigation';
import { apiQuery } from '../../../state';
import type { useIntegrationScreen } from '../hooks';
import { IntegrationInstallationFlow } from '../IntegrationInstallationFlow';
import type { IntegrationInstallationFlowStep } from '../IntegrationInstallationFlow/types';

/**
 * Dialog for the generic installation flow in the context of a integration screen
 *
 * Handles the installation and configuration flow for both sites and spaces
 */
export function IntegrationInstallationDialogContent(props: {
    screen: ReturnType<typeof useIntegrationScreen>;
    integration: api.Integration;
}) {
    const { screen, integration } = props;

    const installationId = getIntegrationInstallationKey(integration.name, {
        organization: screen.organization,
    });

    const { data: installation } = useSuspenseQuery(
        apiQuery('getIntegrationInstallationById', {
            integrationName: integration.name,
            installationId,
        })
    );

    return <InstallIntegrationDialogWithInstallation {...props} installation={installation} />;
}

/**
 * Dialog for the generic installation flow in the context of a integration screen
 *
 * Handles the installation and configuration flow for both sites and spaces
 */
function InstallIntegrationDialogWithInstallation(props: {
    screen: ReturnType<typeof useIntegrationScreen>;
    integration: api.Integration;
    installation: api.IntegrationInstallation;
}) {
    const { screen, integration, installation } = props;
    const { navigate } = useNavigation();

    assertIsDefined(screen.installation, "Installation doesn't exist in screen state");

    const [{ data: spaceInstallation }, { data: siteInstallation }] = useSuspenseQueries({
        queries: [
            apiQuery(
                'getIntegrationSpaceInstallation',
                screen.installation.step === 'configure' &&
                    screen.installation.context?.type === 'space'
                    ? {
                          integrationName: integration.name,
                          installationId: installation.id,
                          spaceId: screen.installation.context.id,
                          extended: true,
                      }
                    : skipToken,
                { nullOnError: true }
            ),
            apiQuery(
                'getIntegrationSiteInstallation',
                screen.installation.step === 'configure' &&
                    screen.installation.context?.type === 'site'
                    ? {
                          integrationName: integration.name,
                          installationId: installation.id,
                          siteId: screen.installation.context.id,
                          extended: true,
                      }
                    : skipToken,
                { nullOnError: true }
            ),
        ] as const,
    });

    /** Step state for the installation flow
     *
     * -- undefined: initial step
     * -- 'configure_content_installation': step to configure the installation
     * */
    const stepState: IntegrationInstallationFlowStep | undefined = (() => {
        switch (screen.installation?.context?.type) {
            case 'site': {
                if (!siteInstallation) {
                    return undefined;
                }
                assertIsExtendedSiteInstallation(siteInstallation);
                return {
                    step: 'configure_content_installation',
                    context: {
                        type: 'site',
                        installation: siteInstallation,
                    },
                };
            }
            case 'space': {
                if (!spaceInstallation) {
                    return undefined;
                }
                assertIsExtendedSpaceInstallation(spaceInstallation);
                return {
                    step: 'configure_content_installation',
                    context: {
                        type: 'space',
                        installation: spaceInstallation,
                    },
                };
            }
            default:
                return undefined;
        }
    })();

    return (
        <IntegrationInstallationFlow
            organizationId={screen.organization}
            integration={integration}
            installation={installation}
            onComplete={() => {
                navigate({ current: { installation: undefined, tab: 'installations' } });
            }}
            stepState={stepState}
        />
    );
}
